import { z } from "zod"

import type { NotificationScope } from "./constants"
import type { PushSchedule, PushSettings } from "./push"
import type { NotificationFilter } from "./registry"
import { NOTIFICATION_SCOPES } from "./constants"
import {
	compactPush,
	isWithinPushSchedule,
	parsePushSettings,
	pushScheduleSchema,
	pushSettingsSchema,
} from "./push"
import {
	getNotificationTypeDefinition,
	NOTIFICATION_TYPES,
	resolveNotificationType,
} from "./registry"

const scopeSchema = z.enum(NOTIFICATION_SCOPES)

export const notificationTypePreferenceSchema = z.object({
	inbox: scopeSchema.optional(),
	desktop: z.boolean().optional(),
	/** null clears the registry's default filter. */
	filter: z.record(z.string(), z.unknown()).nullable().optional(),
	email: scopeSchema.optional(),
	slack: scopeSchema.optional(),
	push: z.boolean().optional(),
})

export type NotificationTypePreference = z.infer<
	typeof notificationTypePreferenceSchema
>

export const notificationSettingsSchema = z.object({
	version: z.literal(1),
	/** One switch for browser popups across every type; stored only when on. */
	desktop: z.boolean().optional(),
	/** Mobile push: master switch and schedule. Phones live in PushDevice. */
	push: pushSettingsSchema.optional(),
	types: z.record(z.string(), notificationTypePreferenceSchema),
})

export type NotificationSettings = z.infer<typeof notificationSettingsSchema>

export const EMPTY_NOTIFICATION_SETTINGS: NotificationSettings = {
	version: 1,
	types: {},
}

/** Tolerant read: bad or unknown entries are dropped instead of failing the fan-out. */
export function parseNotificationSettings(raw: unknown): NotificationSettings {
	if (!raw || typeof raw !== "object") return EMPTY_NOTIFICATION_SETTINGS
	const desktop = (raw as { desktop?: unknown }).desktop === true
	const rawTypes = (raw as { types?: unknown }).types
	const types: NotificationSettings["types"] = {}
	if (rawTypes && typeof rawTypes === "object") {
		for (const [key, value] of Object.entries(rawTypes)) {
			const parsed = notificationTypePreferenceSchema.safeParse(value)
			if (parsed.success) types[key] = parsed.data
		}
	}
	const push = parsePushSettings((raw as { push?: unknown }).push)
	return {
		version: 1,
		...(desktop && { desktop: true }),
		...(push && { push }),
		types,
	}
}

/** Every top-level field except `types`, so a per-type write never drops the rest. */
function baseOf(settings: NotificationSettings): NotificationSettings {
	return {
		version: 1,
		...(settings.desktop && { desktop: true }),
		...(settings.push && { push: settings.push }),
		types: settings.types,
	}
}

/** Browser popups are off until the member turns them on. */
export function desktopNotificationsEnabled(
	settings: NotificationSettings,
): boolean {
	return settings.desktop === true
}

export function setDesktopNotifications(
	settings: NotificationSettings,
	enabled: boolean,
): NotificationSettings {
	const { desktop: _desktop, ...rest } = baseOf(settings)
	return { ...rest, ...(enabled && { desktop: true }) }
}

export function pushNotificationsEnabled(settings: NotificationSettings) {
	return settings.push?.enabled !== false
}

/** Replaces the push block; an empty one is removed so the row stays overrides-only. */
export function setPushSettings(
	settings: NotificationSettings,
	push: PushSettings | undefined,
): NotificationSettings {
	const { push: _push, ...rest } = baseOf(settings)
	const next = push ? compactPush(push) : undefined
	return { ...rest, ...(next && { push: next }) }
}

export function setPushEnabled(
	settings: NotificationSettings,
	enabled: boolean,
): NotificationSettings {
	return setPushSettings(settings, {
		...settings.push,
		enabled: enabled ? undefined : false,
	})
}

export function setPushSchedule(
	settings: NotificationSettings,
	schedule: PushSchedule,
): NotificationSettings {
	return setPushSettings(settings, { ...settings.push, schedule })
}

/** The mobile settings write: either switch, the schedule, or both. */
export function applyPushPreferenceArgs(
	settings: NotificationSettings,
	args: { enabled?: boolean; schedule?: PushSchedule },
): NotificationSettings {
	let next = settings
	if (args.enabled !== undefined) next = setPushEnabled(next, args.enabled)
	if (args.schedule) {
		next = setPushSchedule(next, pushScheduleSchema.parse(args.schedule))
	}
	return next
}

/** Whether a notification of `type` the inbox already delivered should also reach the phone at `at`. */
export function shouldSendPush(
	settings: NotificationSettings,
	type: string,
	at: Date,
): boolean {
	if (!pushNotificationsEnabled(settings)) return false
	if (!resolveTypePreference(type, settings).push) return false
	return isWithinPushSchedule(settings.push?.schedule, at)
}

const PUSH_DEFAULT = true

export interface ResolvedTypePreference {
	inbox: NotificationScope
	desktop: boolean
	/** Mobile push, on by default; the inbox scope still decides whether there is anything to push. */
	push: boolean
	filter: NotificationFilter | null
}

function overrideFor(
	settings: NotificationSettings,
	type: string,
): NotificationTypePreference | undefined {
	const current = resolveNotificationType(type)
	if (!current) return undefined
	if (settings.types[current]) return settings.types[current]
	const aliases = getNotificationTypeDefinition(current)?.aliases ?? []
	for (const alias of aliases) {
		if (settings.types[alias]) return settings.types[alias]
	}
	return undefined
}

/** Registry defaults merged with the member's override, clamped to the type's allowed scopes. */
export function resolveTypePreference(
	type: string,
	settings: NotificationSettings,
): ResolvedTypePreference {
	const def = getNotificationTypeDefinition(type)
	if (!def) return { inbox: "off", desktop: false, push: false, filter: null }
	const override = overrideFor(settings, type)

	let inbox = override?.inbox ?? def.defaults.inbox
	if (!def.scopes.includes(inbox)) {
		inbox = def.scopes.includes("involved") ? "involved" : "off"
	}

	const filter =
		override?.filter !== undefined
			? override.filter
			: (def.defaults.filter ?? null)

	return {
		inbox,
		desktop: override?.desktop ?? def.defaults.desktop,
		push: override?.push ?? PUSH_DEFAULT,
		filter:
			inbox === "all" && filter && Object.keys(filter).length > 0
				? filter
				: null,
	}
}

/**
 * Applies one type's patch and prunes unknown types. A value equal to the
 * registry default is removed, so later default changes still reach this member.
 */
export function patchNotificationSettings(
	settings: NotificationSettings,
	type: string,
	patch: NotificationTypePreference,
): NotificationSettings {
	const key = resolveNotificationType(type)
	if (!key) return settings
	const def = NOTIFICATION_TYPES[key]

	const next: NotificationTypePreference = { ...settings.types[key], ...patch }
	if (next.inbox === def.defaults.inbox) delete next.inbox
	if (next.desktop === def.defaults.desktop) delete next.desktop
	if (next.push === PUSH_DEFAULT) delete next.push
	if (
		next.filter !== undefined &&
		stableStringify(next.filter) ===
			stableStringify(def.defaults.filter ?? null)
	) {
		delete next.filter
	}
	for (const k of Object.keys(next) as (keyof NotificationTypePreference)[]) {
		if (next[k] === undefined) delete next[k]
	}

	const types: NotificationSettings["types"] = {}
	for (const [k, v] of Object.entries(settings.types)) {
		const current = resolveNotificationType(k)
		if (current && current !== key) types[current] = v
	}
	if (Object.keys(next).length > 0) types[key] = next
	return { ...baseOf(settings), types }
}

/** Key-order independent JSON, so identical filters hash the same. */
export function stableStringify(value: unknown): string {
	if (value === null || typeof value !== "object") return JSON.stringify(value)
	if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`
	const entries = Object.entries(value as Record<string, unknown>)
		.filter(([, v]) => v !== undefined)
		.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
	return `{${entries
		.map(([k, v]) => `${JSON.stringify(k)}:${stableStringify(v)}`)
		.join(",")}}`
}
